uniform vec2 uResolution;
uniform vec3 uColor;
uniform vec3 uColor2;
uniform float uTime;
uniform float uScrollVelocity;
uniform float uScale;
uniform float uAmplitude;
uniform int uOctaves;
uniform float uLacunarity;
uniform float uIntensity;


varying vec2 vUv;

const float SCALE = 6.8;
const float AMPLITUDE = 4.4;
const int OCTAVES = 3;
const float LACUNARITY = 0.4;
const float GAIN = 1.5;

float inverseLerp(float v, float minValue, float maxValue) {
    return (v - minValue) / (maxValue - minValue);
}

float remap( float v, float inMin, float inMax, float outMin, float outMax ) {
    float t = inverseLerp(v, inMin, inMax);
    return mix(outMin, outMax, t);
}


// Description : Array and textureless GLSL 2D/3D/4D simplex 
//               noise functions.
//      Author : Ian McEwan, Ashima Arts.
//  Maintainer : stegu
//     Lastmod : 20201014 (stegu)
//     License : Copyright (C) 2011 Ashima Arts. All rights reserved.
//               Distributed under the MIT License. See LICENSE file.
//               https://github.com/ashima/webgl-noise
//               https://github.com/stegu/webgl-noise
// 

vec3 mod289(vec3 x) {
    return x - floor(x * (1.0 / 289.0)) * 289.0;
}
vec4 mod289(vec4 x) {
    return x - floor(x * (1.0 / 289.0)) * 289.0;
}
vec4 permute(vec4 x) {
   return mod289(((x*34.0)+1.0)*x);
}
vec4 taylorInvSqrt(vec4 r) {
    return 1.79284291400159 - 0.85373472095314 * r;
}

float snoise(vec3 v) {
  const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
  const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i  = floor(v + dot(v, C.yyy) );
  vec3 x0 =   v - i + dot(i, C.xxx) ;
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min( g.xyz, l.zxy );
  vec3 i2 = max( g.xyz, l.zxy );
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i); 
  vec4 p = permute( permute( permute( 
              i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
            + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));
  float n_ = 0.142857142857;
  vec3  ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_ );
  vec4 x = x_ *ns.x + ns.yyyy;
  vec4 y = y_ *ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4( x.xy, y.xy );
  vec4 b1 = vec4( x.zw, y.zw );
  vec4 s0 = floor(b0)*2.0 + 1.0;
  vec4 s1 = floor(b1)*2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
  vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;
  vec3 p0 = vec3(a0.xy,h.x);
  vec3 p1 = vec3(a0.zw,h.y);
  vec3 p2 = vec3(a1.xy,h.z);
  vec3 p3 = vec3(a1.zw,h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;
  vec4 m = max(0.5 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 105.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3) ) );
}

float fbm(in vec2 p) {
    float t = uTime * 0.04;
    float value = 0.0;
    float amplitude = uAmplitude;
    vec3 q = vec3(p.xy, t);
    
    for (int i = 0; i < uOctaves; i++) {
        float n = snoise(q);
        n = abs(n);
        value += amplitude * n;
        q.xy *= uLacunarity;
        amplitude *= uIntensity;
    }
    return value;
}

float getNoise(in vec2 p) {
    return fbm(p);
}

vec3 gamma(in vec3 color)
{
    return pow(max(color, 0.0), vec3(1.0 / 2.2));
}


void main() {
  // vec2 uv = vUv;

  vec3 color = uColor;
  // color = color;

  // gl_FragColor = vec4(color, 1.0);
  // gl_FragColor = vec4(uv, 1.0, 1.0);


  vec2 uv = (2.0 * gl_FragCoord.xy - uResolution.xy) / uResolution.y;
  vec2 offset = vec2(10.0, uScrollVelocity * 0.03);  
  vec2 p = uScale * uv + offset;
  float n = getNoise(p);
  n = 0.02 / n;
  n = pow(n, 0.84);
  color = vec3(n);

  color = mix(
    // uColor / 2.5,
    // uColor2,
    uColor / 4.5,
    uColor2 * 2.,
    // uColor2 * 5.,
    smoothstep(0.1, 2.4, n)
  );

  gl_FragColor = vec4(color, 1.0);

  #include <colorspace_fragment>
// #include <tonemapping_fragment>
}